import { getSiteAuthRuntime, getStoredToken } from './bliss-app-auth';

interface PaymentConfigResponse {
  stripe_mode: 'test' | 'live';
  publishable_key: string | null;
  stripe_account_id: string | null;
  stripe_connect_account_configured: boolean;
  payments_ready: boolean;
}

export interface DonationIntentResponse {
  payment_intent_id: string;
  client_secret: string;
  status: string;
  stripe_account_id: string | null;
  amount_cents: number;
  application_fee_amount?: number | null;
}

export interface DonationSubscriptionResponse {
  subscription_id?: string | null;
  payment_intent_id?: string | null;
  client_secret: string;
  status?: string | null;
  stripe_account_id: string | null;
}

export interface EventTicketIntentResponse {
  order_id: string;
  payment_intent_id: string;
  client_secret: string;
  status: string;
  stripe_account_id: string | null;
  amount_cents: number;
  base_amount_cents: number;
  customer_fee_cents: number;
  estimated_stripe_fee_cents: number;
  application_fee_amount: number;
  quantity: number;
  ticket: {
    id: string;
    name: string;
    category: string;
  };
  event: {
    id: string;
    slug: string;
    title: string;
  };
}

export interface EventTicketOrderStatusResponse {
  order: {
    id: string;
    event_id: string;
    event_slug: string;
    event_title: string;
    ticket_id: string;
    ticket_name: string;
    ticket_category: string;
    ticket_quantity: number;
    purchaser_name: string | null;
    purchaser_email: string;
    currency: string;
    base_amount_cents: number;
    processing_fee_cents: number;
    charged_amount_cents: number;
    estimated_stripe_fee_cents: number;
    application_fee_cents: number;
    stripe_payment_intent_id: string | null;
    stripe_payment_intent_status: string | null;
    stripe_refund_id: string | null;
    payment_status: 'pending' | 'processing' | 'paid' | 'refunded' | 'failed' | 'canceled';
    fulfillment_status: 'pending' | 'ready' | 'fulfilled' | 'canceled';
    paid_at: number | null;
    refunded_at: number | null;
    last_error: string | null;
    created_at: number;
    updated_at: number;
  };
}

function getErrorMessage(payload: unknown, fallback: string): string {
  if (!payload || typeof payload !== 'object') return fallback;

  const source = payload as {
    error?: string | { message?: string };
    message?: string;
    data?: { message?: string };
  };

  if (typeof source.error === 'string' && source.error) return source.error;
  if (source.error && typeof source.error === 'object' && typeof source.error.message === 'string') {
    return source.error.message;
  }
  if (typeof source.message === 'string' && source.message) return source.message;
  if (source.data && typeof source.data.message === 'string' && source.data.message) return source.data.message;
  return fallback;
}

async function readResponseJson(response: Response): Promise<unknown> {
  return response.json().catch(() => null);
}

async function requestPaymentsApi<T>(path: string, options: RequestInit = {}): Promise<T> {
  const runtime = await getSiteAuthRuntime();
  const token = getStoredToken();
  const response = await fetch(`${runtime.apiBaseUrl}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options.headers,
    },
  });

  const payload = await readResponseJson(response);
  if (!response.ok) {
    throw new Error(getErrorMessage(payload, 'The payment service could not be reached right now.'));
  }

  const source = payload as { data?: T };
  return (source.data ?? payload) as T;
}

export async function getSitePaymentConfig(): Promise<PaymentConfigResponse> {
  return requestPaymentsApi<PaymentConfigResponse>('/payments/config');
}

export async function createEventTicketIntent(input: {
  eventId: string;
  ticketId: string;
  quantity?: number;
  purchaserName?: string;
  purchaserEmail: string;
  vendorProfileType?: 'vendor' | 'resource_vendor';
  vendorBusinessName?: string;
  vendorContactName?: string;
  vendorEmail?: string;
  vendorPhone?: string;
  vendorWebsiteUrl?: string;
  vendorInstagramHandle?: string;
  vendorDescription?: string;
  vendorBoothDetails?: string;
}): Promise<EventTicketIntentResponse> {
  return requestPaymentsApi<EventTicketIntentResponse>('/payments/events/intent', {
    method: 'POST',
    body: JSON.stringify({
      event_id: input.eventId,
      ticket_id: input.ticketId,
      quantity: input.quantity ?? 1,
      purchaser_name: input.purchaserName?.trim() || '',
      purchaser_email: input.purchaserEmail.trim(),
      vendor_profile_type: input.vendorProfileType,
      vendor_business_name: input.vendorBusinessName?.trim() || '',
      vendor_contact_name: input.vendorContactName?.trim() || '',
      vendor_email: input.vendorEmail?.trim() || '',
      vendor_phone: input.vendorPhone?.trim() || '',
      vendor_website_url: input.vendorWebsiteUrl?.trim() || '',
      vendor_instagram_handle: input.vendorInstagramHandle?.trim() || '',
      vendor_description: input.vendorDescription?.trim() || '',
      vendor_booth_details: input.vendorBoothDetails?.trim() || '',
    }),
  });
}

export async function getEventTicketOrderStatus(input: {
  orderId?: string;
  paymentIntentId?: string;
}): Promise<EventTicketOrderStatusResponse> {
  const params = new URLSearchParams();
  if (input.orderId) {
    params.set('order_id', input.orderId);
  }
  if (input.paymentIntentId) {
    params.set('payment_intent', input.paymentIntentId);
  }

  return requestPaymentsApi<EventTicketOrderStatusResponse>(`/payments/events/order-status?${params.toString()}`);
}

export async function createDonationIntent(input: {
  amount: number;
  currency?: string;
  donorName?: string;
  donorEmail: string;
  applicationFeeAmount?: number;
  metadata?: Record<string, string>;
}): Promise<DonationIntentResponse> {
  return requestPaymentsApi<DonationIntentResponse>('/payments/donations/intent', {
    method: 'POST',
    body: JSON.stringify({
      amount: input.amount,
      currency: input.currency ?? 'usd',
      donor_name: input.donorName?.trim() || '',
      donor_email: input.donorEmail.trim(),
      application_fee_amount: input.applicationFeeAmount ?? 0,
      metadata: input.metadata ?? {},
    }),
  });
}

export async function createDonationSubscription(input: {
  amount: number;
  currency?: string;
  donorName?: string;
  donorEmail: string;
  applicationFeePercent?: number;
  metadata?: Record<string, string>;
  appUserEmail?: string;
}): Promise<DonationSubscriptionResponse> {
  return requestPaymentsApi<DonationSubscriptionResponse>('/payments/donations/subscription', {
    method: 'POST',
    body: JSON.stringify({
      amount: input.amount,
      currency: input.currency ?? 'usd',
      donor_name: input.donorName?.trim() || '',
      donor_email: input.donorEmail.trim(),
      app_user_email: input.appUserEmail?.trim() || '',
      application_fee_percent: input.applicationFeePercent ?? 2.2,
      metadata: input.metadata ?? {},
    }),
  });
}
